import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewConnectedPillarFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const shouldRemoveItem = (item, filterTag: string): boolean => {
    if (typeof item !== 'object' || item === null) return false
    const tags = item.fields?.personalizationTags
    return Array.isArray(tags) && tags.length > 0 && !tags.includes(filterTag)
  }

  // Filter an array by removing items and recursing into each item
  const filterArrayByTag = (items, tag: string, previewConfig: PreviewConfig) => {
    return items
      .filter((item) => !shouldRemoveItem(item, tag))
      .map((item) => filterByPersonalizationTag(item, previewConfig))
  }

  // Filter an object field by field, recursing into nested structures
  const filterObjectByTag = (obj: Record<string, any>, tag: string, previewConfig: PreviewConfig) => {
    const filtered: Record<string, any> = {}

    Object.entries(obj).forEach(([key, value]) => {
      // Skip items marked for removal
      if (shouldRemoveItem(value, tag)) return

      // Special case: 'content' field with nested content array
      if (isRichTextContent(value)) {
        filtered[key] = {
          ...(value as any),
          content: filterContentArray((value as any).content, tag, previewConfig),
        }
        return
      }

      // Recurse based on value type
      if (Array.isArray(value)) {
        filtered[key] = filterArrayByTag(value, tag, previewConfig)
      } else if (typeof value === 'object' && value !== null) {
        filtered[key] = filterByPersonalizationTag(value, previewConfig)
      } else {
        filtered[key] = value // Keep primitives as-is
      }
    })

    return filtered
  }

  const filterContentArray = (contentArray, filterTag: string, fullConfig: PreviewConfig) => {
    if (!Array.isArray(contentArray)) return contentArray

    return contentArray
      .filter((item) => hasPersonalizationTag(item.data?.target, filterTag))
      .map((item) => filterByPersonalizationTag(item, fullConfig))
  }

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string> | any,
    previewConfig: PreviewConfig,
  ) => {
    const tag = previewConfig.previewPersonalizationTag
    if (!tag) return entries

    // Base cases: no recursion needed
    if (Array.isArray(entries)) {
      return filterArrayByTag(entries, tag, previewConfig)
    }

    if (typeof entries === 'object' && entries !== null) {
      return filterObjectByTag(entries, tag, previewConfig)
    }

    // Primitives: return unchanged
    return entries
  }

  const extractPanelsFromBlogArticles = (articles): LocalePageEntry[] => {
    const panels: LocalePageEntry[] = []

    articles.forEach((article) => {
      // Add the article itself if it has sys.revision (is a panel)
      if (article?.sys?.revision !== undefined) {
        panels.push(article as LocalePageEntry)
      }

      // Extract panels from article fields
      if (article?.fields && typeof article.fields === 'object') {
        Object.values(article.fields).forEach((fieldValue: any) => {
          if (fieldValue?.sys?.revision !== undefined) {
            panels.push(fieldValue as LocalePageEntry)
          }
        })
      }
    })

    return panels
  }

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const { items } = entries
    if (!items?.length) return []

    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return []

    const panels: PanelInfo[] = []

    Object.entries(firstPage).forEach(([key, value]) => {
      if (value?.nodeType && Array.isArray(value?.content)) {
        extractPanelsFromRichTextContent(value).forEach((panel) => panels.push(getPanelInfo(panel)))
        return
      }

      if (value?.sys?.contentType?.sys?.id === 'blogArticle' && Array.isArray(value)) {
        extractPanelsFromBlogArticles(value as any[]).forEach((panel) => panels.push(getPanelInfo(panel)))
        return
      }

      // Handle regular panel fields
      const panelId = extractPanelId(value)
      if (panelId) {
        panels.push(getPanelInfo(value as LocalePageEntry))
      }
    })

    // add parent page info to panels
    panels.push(getPanelInfo(entries.items[0], { isParentPage: true }))

    return panels
  }

  const filterDescriptionContent = (description, panelDisplayInfo: Record<string, { show: boolean | string }>) => {
    if (!Array.isArray(description?.content)) return description

    const filteredContent = description.content
      .map((node) => {
        if (!Array.isArray(node.content)) return node

        return {
          ...node,
          content: node.content.filter((item) => {
            const targetId = item.data?.target?.sys?.id
            return !targetId || panelDisplayInfo[targetId]?.show !== false
          }),
        }
      })
      .filter((node) => !Array.isArray(node.content) || node.content.length > 0)

    return {
      ...description,
      content: filteredContent,
    }
  }

  const filterBlogArticles = (articles, panelDisplayInfo: Record<string, { show: boolean | string }>) => {
    return articles
      .filter((article) => {
        // Keep if no sys.id or if display info says to show
        const articleId = article?.sys?.id
        return !articleId || panelDisplayInfo[articleId]?.show !== false
      })
      .map((article) => {
        // Filter article fields
        if (!article?.fields) return article

        const filteredFields = {}
        Object.entries(article.fields).forEach(([fieldKey, fieldValue]) => {
          const fieldId = (fieldValue as any)?.sys?.id
          if (!fieldId || panelDisplayInfo[fieldId]?.show !== false) {
            filteredFields[fieldKey] = fieldValue
          }
        })

        return {
          ...article,
          fields: filteredFields,
        }
      })
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
    options?: { panels?: PanelInfo[]; scheduledActions?: Record<string, any> },
  ) => {
    const { items } = entries
    if (!items?.length) return entries

    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return entries

    if (shouldHidePreviewPayload(previewConfig, panelDisplayInfo, options)) {
      entries.items = []
      return entries
    }
    Object.entries(firstPage).forEach(([key, value]) => {
      if (value?.nodeType && Array.isArray(value?.content)) {
        firstPage[key] = filterDescriptionContent(value, panelDisplayInfo)
        return
      }

      if (value?.sys?.contentType?.sys?.id === 'blogArticle' && Array.isArray(value)) {
        firstPage[key] = filterBlogArticles(value as any[], panelDisplayInfo)
        return
      }

      // Handle single panel fields
      const itemId = extractPanelId(value)
      if (itemId && panelDisplayInfo[itemId]?.show === false) {
        delete firstPage[key]
      }
    })

    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
